import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  getBusinessStats,
  bulkUpdateMyAds,
  importAdsFromRows,
  saveOpeningHours,
  getMyOpeningHours,
  listMembers,
  inviteMember,
  removeMember,
  myEmployers,
  staffUpdateAd,
} from "@/lib/business-tools.functions";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useLocale } from "@/lib/i18n/locale-context";
import { formatPrice } from "@/lib/i18n/format";
import { parseCsv } from "@/lib/csv";

export const Route = createFileRoute("/_authenticated/foretagsverktyg")({
  head: () => ({
    meta: [
      { title: "Företagsverktyg – Sellavo" },
      { name: "description", content: "Statistik, masshantering, import, anställda och öppettider för ditt företag på Sellavo." },
      { property: "og:title", content: "Företagsverktyg – Sellavo" },
      { property: "og:description", content: "Statistik, masshantering, import, anställda och öppettider för företag." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: BusinessTools,
});

const T = {
  sv: {
    title: "Företagsverktyg", stats: "Statistik", ads: "Annonser", import: "Importera", staff: "Anställda", hours: "Öppettider",
    month: "Månad", views: "Visningar", saves: "Sparade", messages: "Meddelanden", sold: "Sålda", none: "Ingen statistik än.",
    selected: "valda", pause: "Pausa", resume: "Återaktivera", change: "Ändra pris", kr: "Belopp (+/-)", pct: "Procent (+/-)",
    inclVat: "Pris inkl. moms", exclVat: "Pris exkl. moms", paused: "Pausad", soldL: "Såld", done: "Uppdaterade", noBiz: "Du behöver en företagsprofil. Skapa den under Mitt konto.",
    csvHelp: "CSV med kolumnerna: title, description, price, category, location, images (bildlänkar separerade med |). Max 50 rader per gång.",
    preview: "Förhandsgranskning", publish: "Publicera", rowsOk: "rader klara", rowsBad: "rader med fel", created: "Publicerade", limit: "hoppades över p.g.a. månadsgränsen",
    invite: "Bjud in", email: "E-post", invited: "Inbjuden", active: "Aktiv", remove: "Ta bort", staffHelp: "Anställda kan pausa, återaktivera och ändra pris på företagets annonser. De kan inte ändra betalning eller företagsuppgifter.",
    employer: "Företag du arbetar för", closed: "Stängt", save: "Spara", saved: "Sparat",
    days: ["Måndag", "Tisdag", "Onsdag", "Torsdag", "Fredag", "Lördag", "Söndag"],
  },
  en: {
    title: "Business tools", stats: "Statistics", ads: "Listings", import: "Import", staff: "Staff", hours: "Opening hours",
    month: "Month", views: "Views", saves: "Saved", messages: "Messages", sold: "Sold", none: "No statistics yet.",
    selected: "selected", pause: "Pause", resume: "Reactivate", change: "Change price", kr: "Amount (+/-)", pct: "Percent (+/-)",
    inclVat: "Price incl. VAT", exclVat: "Price excl. VAT", paused: "Paused", soldL: "Sold", done: "Updated", noBiz: "You need a business profile. Create one under My account.",
    csvHelp: "CSV with columns: title, description, price, category, location, images (image links separated by |). Max 50 rows at a time.",
    preview: "Preview", publish: "Publish", rowsOk: "rows ready", rowsBad: "rows with errors", created: "Published", limit: "skipped due to monthly limit",
    invite: "Invite", email: "Email", invited: "Invited", active: "Active", remove: "Remove", staffHelp: "Staff can pause, reactivate and change prices on the company's listings. They cannot change payment or company details.",
    employer: "Companies you work for", closed: "Closed", save: "Save", saved: "Saved",
    days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
  },
  es: {
    title: "Herramientas de empresa", stats: "Estadísticas", ads: "Anuncios", import: "Importar", staff: "Empleados", hours: "Horario",
    month: "Mes", views: "Visitas", saves: "Guardados", messages: "Mensajes", sold: "Vendidos", none: "Aún no hay estadísticas.",
    selected: "seleccionados", pause: "Pausar", resume: "Reactivar", change: "Cambiar precio", kr: "Importe (+/-)", pct: "Porcentaje (+/-)",
    inclVat: "Precio con IVA", exclVat: "Precio sin IVA", paused: "Pausado", soldL: "Vendido", done: "Actualizados", noBiz: "Necesitas un perfil de empresa. Créalo en Mi cuenta.",
    csvHelp: "CSV con columnas: title, description, price, category, location, images (enlaces separados por |). Máx. 50 filas cada vez.",
    preview: "Vista previa", publish: "Publicar", rowsOk: "filas listas", rowsBad: "filas con errores", created: "Publicados", limit: "omitidos por el límite mensual",
    invite: "Invitar", email: "Correo", invited: "Invitado", active: "Activo", remove: "Quitar", staffHelp: "Los empleados pueden pausar, reactivar y cambiar precios de los anuncios. No pueden cambiar pagos ni datos de la empresa.",
    employer: "Empresas para las que trabajas", closed: "Cerrado", save: "Guardar", saved: "Guardado",
    days: ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"],
  },
};
type Tx = (typeof T)["sv"];
const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;

function BusinessTools() {
  const { lang } = useLocale();
  const tx = T[lang as keyof typeof T] ?? T.sv;
  const statsFn = useServerFn(getBusinessStats);
  const stats = useQuery({ queryKey: ["biz-stats"], queryFn: () => statsFn(), retry: false });
  const empFn = useServerFn(myEmployers);
  const employers = useQuery({ queryKey: ["biz-employers"], queryFn: () => empFn() });

  return (
    <main className="mx-auto w-full max-w-5xl px-4 pb-16 pt-6 sm:px-6 lg:px-8">
      <h1 className="font-heading text-3xl font-bold">{tx.title}</h1>
      {(employers.data?.length ?? 0) > 0 && <EmployerSection tx={tx} data={employers.data!} />}
      {stats.isError ? (
        <p className="mt-6 rounded-lg border border-dashed border-border p-6 text-muted-foreground">
          {tx.noBiz} <Link to="/konto" className="text-primary underline">→</Link>
        </p>
      ) : (
        <Tabs defaultValue="stats" className="mt-6">
          <TabsList className="flex h-auto flex-wrap">
            <TabsTrigger value="stats">{tx.stats}</TabsTrigger>
            <TabsTrigger value="ads">{tx.ads}</TabsTrigger>
            <TabsTrigger value="import">{tx.import}</TabsTrigger>
            <TabsTrigger value="staff">{tx.staff}</TabsTrigger>
            <TabsTrigger value="hours">{tx.hours}</TabsTrigger>
          </TabsList>
          <TabsContent value="stats"><StatsTab tx={tx} months={stats.data?.months ?? []} /></TabsContent>
          <TabsContent value="ads"><AdsTab tx={tx} ads={stats.data?.ads ?? []} /></TabsContent>
          <TabsContent value="import"><ImportTab tx={tx} /></TabsContent>
          <TabsContent value="staff"><StaffTab tx={tx} /></TabsContent>
          <TabsContent value="hours"><HoursTab tx={tx} /></TabsContent>
        </Tabs>
      )}
    </main>
  );
}

function StatsTab({ tx, months }: { tx: Tx; months: { month: string; views: number; saves: number; messages: number; sold: number }[] }) {
  if (!months.length) return <p className="mt-4 text-muted-foreground">{tx.none}</p>;
  return (
    <div className="mt-4 overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead className="bg-muted text-left"><tr>{[tx.month, tx.views, tx.saves, tx.messages, tx.sold].map((h) => <th key={h} className="p-3">{h}</th>)}</tr></thead>
        <tbody>{months.map((m) => (
          <tr key={m.month} className="border-t border-border tabular-nums">
            <td className="p-3 font-medium">{m.month}</td><td className="p-3">{m.views}</td><td className="p-3">{m.saves}</td><td className="p-3">{m.messages}</td><td className="p-3">{m.sold}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function AdsTab({ tx, ads }: { tx: Tx; ads: { slug: string; title: string; price: number; paused: boolean; sold: boolean; archived: boolean; priceIncludesVat: boolean; country: string }[] }) {
  const qc = useQueryClient();
  const bulk = useServerFn(bulkUpdateMyAds);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [val, setVal] = useState("");
  const list = ads.filter((a) => !a.archived);
  const run = async (action: "pause" | "resume" | "price_amount" | "price_percent" | "vat_incl" | "vat_excl") => {
    const value = action.startsWith("price") ? Number(val) : undefined;
    if (value !== undefined && !Number.isFinite(value)) return;
    try {
      const r = await bulk({ data: { slugs: [...sel], action, value } });
      toast.success(`${tx.done}: ${r.changed}`);
      qc.invalidateQueries({ queryKey: ["biz-stats"] });
    } catch (e) { toast.error((e as Error).message); }
  };
  const all = sel.size === list.length && list.length > 0;
  return (
    <div className="mt-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card p-3">
        <span className="text-sm text-muted-foreground">{sel.size} {tx.selected}</span>
        <Button size="sm" variant="outline" disabled={!sel.size} onClick={() => run("pause")}>{tx.pause}</Button>
        <Button size="sm" variant="outline" disabled={!sel.size} onClick={() => run("resume")}>{tx.resume}</Button>
        <Input className="h-8 w-28" value={val} onChange={(e) => setVal(e.target.value)} placeholder="+/-" inputMode="decimal" />
        <Button size="sm" variant="outline" disabled={!sel.size || !val} onClick={() => run("price_amount")}>{tx.kr}</Button>
        <Button size="sm" variant="outline" disabled={!sel.size || !val} onClick={() => run("price_percent")}>{tx.pct}</Button>
        <Button size="sm" variant="outline" disabled={!sel.size} onClick={() => run("vat_incl")}>{tx.inclVat}</Button>
        <Button size="sm" variant="outline" disabled={!sel.size} onClick={() => run("vat_excl")}>{tx.exclVat}</Button>
      </div>
      <div className="rounded-lg border border-border">
        <label className="flex items-center gap-3 border-b border-border p-3 text-sm font-medium">
          <Checkbox checked={all} onCheckedChange={(c) => setSel(c ? new Set(list.map((a) => a.slug)) : new Set())} /> {list.length} {tx.ads.toLowerCase()}
        </label>
        {list.map((a) => (
          <label key={a.slug} className="flex items-center gap-3 border-b border-border p-3 text-sm last:border-0">
            <Checkbox checked={sel.has(a.slug)} onCheckedChange={(c) => { const n = new Set(sel); c ? n.add(a.slug) : n.delete(a.slug); setSel(n); }} />
            <span className="min-w-0 flex-1 truncate">{a.title}</span>
            {a.paused && <span className="text-xs text-muted-foreground">{tx.paused}</span>}
            {a.sold && <span className="text-xs text-muted-foreground">{tx.soldL}</span>}
            <span className="tabular-nums">{formatPrice(a.price, a.country === "ES" ? "ES" : "SE")}</span>
            <span className="hidden text-xs text-muted-foreground sm:inline">{a.priceIncludesVat ? tx.inclVat : tx.exclVat}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

type Row = { title: string; description: string; price: number; category?: string; location: string; images: string[] };

function ImportTab({ tx }: { tx: Tx }) {
  const { country } = useLocale();
  const qc = useQueryClient();
  const importFn = useServerFn(importAdsFromRows);
  const [rows, setRows] = useState<{ row: Row; error: string | null }[]>([]);
  const [busy, setBusy] = useState(false);
  const onFile = async (f: File) => {
    const records = parseCsv(await f.text()).slice(0, 50);
    setRows(records.map((r) => {
      const row: Row = {
        title: (r.title ?? "").trim(), description: (r.description ?? "").trim(),
        price: Math.round(Number(String(r.price ?? "").replace(/\s/g, "").replace(",", "."))),
        category: (r.category ?? "").trim() || undefined, location: (r.location ?? "").trim(),
        images: String(r.images ?? "").split("|").map((s) => s.trim()).filter(Boolean),
      };
      const error = row.title.length < 3 ? "title" : row.description.length < 10 ? "description" : !Number.isFinite(row.price) || row.price < 0 ? "price" : row.location.length < 2 ? "location" : !row.images.length || !row.images.every((u) => /^https?:\/\//.test(u)) ? "images" : null;
      return { row, error };
    }));
  };
  const ok = rows.filter((r) => !r.error);
  const publish = async () => {
    setBusy(true);
    try {
      const r = await importFn({ data: { rows: ok.map((x) => x.row), country: country === "ES" ? "ES" : "SE" } });
      toast.success(`${tx.created}: ${r.created.length}${r.skippedForLimit ? ` · ${r.skippedForLimit} ${tx.limit}` : ""}${r.failed.length ? ` · ${r.failed.length} ${tx.rowsBad}` : ""}`);
      setRows([]);
      qc.invalidateQueries({ queryKey: ["biz-stats"] });
    } catch (e) { toast.error((e as Error).message); } finally { setBusy(false); }
  };
  return (
    <div className="mt-4 space-y-4">
      <p className="text-sm text-muted-foreground">{tx.csvHelp}</p>
      <Input type="file" accept=".csv,text/csv" onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
      {rows.length > 0 && (
        <>
          <p className="text-sm font-medium">{tx.preview}: {ok.length} {tx.rowsOk} · {rows.length - ok.length} {tx.rowsBad}</p>
          <ul className="divide-y divide-border rounded-lg border border-border text-sm">
            {rows.map((r, i) => (
              <li key={i} className="flex items-center gap-3 p-3">
                {r.row.images[0] && <img src={r.row.images[0]} alt="" className="h-10 w-10 rounded object-cover" />}
                <span className="min-w-0 flex-1 truncate">{r.row.title || "—"}</span>
                <span className="tabular-nums">{Number.isFinite(r.row.price) ? r.row.price : "?"}</span>
                {r.error && <span className="text-xs text-destructive">{r.error}</span>}
              </li>
            ))}
          </ul>
          <Button disabled={!ok.length || busy} onClick={publish}>{tx.publish} ({ok.length})</Button>
        </>
      )}
    </div>
  );
}

function StaffTab({ tx }: { tx: Tx }) {
  const qc = useQueryClient();
  const listFn = useServerFn(listMembers);
  const inviteFn = useServerFn(inviteMember);
  const removeFn = useServerFn(removeMember);
  const { data } = useQuery({ queryKey: ["biz-members"], queryFn: () => listFn() });
  const [email, setEmail] = useState("");
  const invite = async () => {
    try { await inviteFn({ data: { email } }); setEmail(""); qc.invalidateQueries({ queryKey: ["biz-members"] }); }
    catch (e) { toast.error((e as Error).message); }
  };
  return (
    <div className="mt-4 space-y-4">
      <p className="text-sm text-muted-foreground">{tx.staffHelp}</p>
      <div className="flex max-w-md gap-2">
        <Input type="email" placeholder={tx.email} value={email} onChange={(e) => setEmail(e.target.value)} />
        <Button disabled={!email} onClick={invite}>{tx.invite}</Button>
      </div>
      <ul className="divide-y divide-border rounded-lg border border-border text-sm">
        {(data ?? []).map((m: any) => (
          <li key={m.id} className="flex items-center gap-3 p-3">
            <span className="flex-1 truncate">{m.invited_email}</span>
            <span className="text-xs text-muted-foreground">{m.status === "active" ? tx.active : tx.invited}</span>
            <Button size="sm" variant="ghost" onClick={async () => { await removeFn({ data: { id: m.id } }); qc.invalidateQueries({ queryKey: ["biz-members"] }); }}>{tx.remove}</Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function HoursTab({ tx }: { tx: Tx }) {
  const getFn = useServerFn(getMyOpeningHours);
  const saveFn = useServerFn(saveOpeningHours);
  const { data } = useQuery({ queryKey: ["biz-hours"], queryFn: () => getFn() });
  const [edit, setEdit] = useState<Record<string, { closed: boolean; open: string; close: string }> | null>(null);
  const hours = edit ?? Object.fromEntries(DAY_KEYS.map((d) => [d, (data?.hours as any)?.[d] ?? { closed: d === "sun", open: "10:00", close: "18:00" }]));
  const set = (d: string, patch: Partial<{ closed: boolean; open: string; close: string }>) => setEdit({ ...hours, [d]: { ...hours[d], ...patch } });
  return (
    <div className="mt-4 max-w-lg space-y-2">
      {DAY_KEYS.map((d, i) => (
        <div key={d} className="flex items-center gap-3 text-sm">
          <span className="w-24">{tx.days[i]}</span>
          <label className="flex items-center gap-1.5"><Checkbox checked={hours[d].closed} onCheckedChange={(c) => set(d, { closed: !!c })} />{tx.closed}</label>
          <Input type="time" className="h-8 w-28" disabled={hours[d].closed} value={hours[d].open} onChange={(e) => set(d, { open: e.target.value })} />
          <span>–</span>
          <Input type="time" className="h-8 w-28" disabled={hours[d].closed} value={hours[d].close} onChange={(e) => set(d, { close: e.target.value })} />
        </div>
      ))}
      <Button className="mt-3" onClick={async () => { try { await saveFn({ data: { hours: hours as never } }); toast.success(tx.saved); } catch (e) { toast.error((e as Error).message); } }}>{tx.save}</Button>
    </div>
  );
}

function EmployerSection({ tx, data }: { tx: Tx; data: { companyName: string; ads: any[] }[] }) {
  const qc = useQueryClient();
  const upd = useServerFn(staffUpdateAd);
  const act = async (slug: string, paused: boolean) => {
    try { await upd({ data: { slug, paused } }); qc.invalidateQueries({ queryKey: ["biz-employers"] }); } catch (e) { toast.error((e as Error).message); }
  };
  return (
    <section className="mt-6 rounded-lg border border-border bg-card p-4">
      <h2 className="font-semibold">{tx.employer}</h2>
      {data.map((c) => (
        <div key={c.companyName} className="mt-3">
          <p className="text-sm font-medium">{c.companyName}</p>
          <ul className="mt-2 divide-y divide-border text-sm">
            {c.ads.map((a) => (
              <li key={a.slug} className="flex items-center gap-3 py-2">
                <Link to="/a/$slug" params={{ slug: a.slug }} className="min-w-0 flex-1 truncate hover:underline">{a.title}</Link>
                <span className="tabular-nums">{formatPrice(a.price, a.country === "ES" ? "ES" : "SE")}</span>
                <Button size="sm" variant="outline" onClick={() => act(a.slug, !a.is_paused)}>{a.is_paused ? tx.resume : tx.pause}</Button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}
